import { getCalApi } from '@calcom/embed-react';
import type { ButtonProps, TooltipProps } from '@montugroup/design-system';
import { Button, Tooltip } from '@montugroup/design-system';
import { useMediaQuery } from '@mui/material';
import type { PropsWithChildren } from 'react';
import { useEffect } from 'react';

import settings from '@/constants/constants';
import { removeChildElements } from '@montugroup/frontend-common';

export type BookingDetails = {
  booking?: {
    uid?: string;
    responses?: {
      name?: string | { firstName?: string; lastName?: string };
      phoneNumber?: string;
    };
    description: string;
    startTime?: string;
    endTime?: string;
    title?: string;
    status?: string;
    attendees?: Array<{
      email: string;
      name: string;
      timeZone: string;
    }>;
  };
  date?: string;
  confirmed?: boolean;
};

const defaultButtonProps = (togglePrimaryColours: boolean): Partial<ButtonProps> => ({
  color: togglePrimaryColours ? 'primary' : 'secondary',
});

interface PrefilledConfig {
  name?: string;
  email?: string;
  phoneNumber?: string;
}

export type Props = PropsWithChildren<{
  link: string;
  formData?: PrefilledConfig;
  onOpen?: () => void;
  disabled?: boolean;
  buttonProps?: Omit<ButtonProps, 'children'>;
  tooltipProps?: Omit<TooltipProps, 'children'>; // only present when the button is disabled
  handleBookingSuccess?: (value?: BookingDetails) => void;
  handleRescheduleSuccess?: (value?: BookingDetails) => void;
  handleCancelSuccess?: (value?: BookingDetails) => void;
  testId?: string;
  togglePrimaryColours?: boolean;
}>;

export default function CalendarPopupButton({
  children,
  link,
  formData,
  onOpen,
  disabled = false,
  buttonProps = {},
  tooltipProps,
  handleBookingSuccess,
  handleRescheduleSuccess,
  handleCancelSuccess,
  testId,
  togglePrimaryColours = false,
}: Props) {
  const allButtonProps = { ...defaultButtonProps(togglePrimaryColours), ...buttonProps, disabled };

  const themeColourScheme = useMediaQuery('(prefers-color-scheme: dark)') ? 'dark' : 'light';

  useEffect(() => {
    (async function initCalendar() {
      const cal = await getCalApi(settings.calendarConfig.embedJsUrl);
      cal('ui', { theme: 'light' });
      if (handleBookingSuccess) {
        cal('on', {
          action: 'bookingSuccessful',
          callback: ({ detail }) => {
            handleBookingSuccess(detail.data as BookingDetails);

            removeChildElements('cal-modal-box');
            document.body.removeAttribute('style'); // this removes overflow hidden style applied by Calcom
          },
        });
      }
      if (handleRescheduleSuccess) {
        cal('on', {
          action: 'rescheduleBookingSuccessful',
          callback: ({ detail }) => {
            handleRescheduleSuccess(detail.data as BookingDetails);

            removeChildElements('cal-modal-box');
            document.body.removeAttribute('style'); // this removes overflow hidden style applied by Calcom
          },
        });
      }
      if (handleCancelSuccess) {
        cal('on', {
          action: 'bookingCancelled',
          callback: ({ detail }) => {
            handleCancelSuccess(detail.data as BookingDetails);

            removeChildElements('cal-modal-box');
            document.body.removeAttribute('style'); // this removes overflow hidden style applied by Calcom
          },
        });
      }

      if (typeof onOpen === 'function') {
        cal('on', { action: 'linkReady', callback: onOpen });
      }
    })();
  }, [onOpen, handleBookingSuccess, handleRescheduleSuccess, handleCancelSuccess]);

  const button = (
    <Button
      data-cal-link={link}
      data-cal-config={JSON.stringify(formData ?? {})}
      data-cal-origin={settings.calendarConfig.calOrigin}
      data-testid={testId || 'book-consultation'}
      data-cal-theme-mode={themeColourScheme}
      {...allButtonProps}
    >
      {children}
    </Button>
  );

  if (disabled && tooltipProps?.title) {
    return <Tooltip {...tooltipProps}>{button}</Tooltip>;
  }

  return button;
}
